import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  ListItemText,
  Chip,
} from '@mui/material';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import ItemSortModal from '../../../components/PosMenu/ItemSortModal';
import { toast } from 'react-toastify';
import confirm from '../../../components/Confirm';
import TranslationInput from '../../../components/PosMenu/TranslationInput';
import FieldSavingIndicator from '../../../components/PosMenu/FieldSavingIndicator';
import useScheduledTimeouts from '../hooks/useScheduledTimeouts';
import {
  createCategory,
  updateCategory,
  deleteCategory,
  clearSelected,
  setSelectedEntity,
} from '../../../redux/actions/posMenu';
import { DEFAULT_CATEGORY, getTranslationValue } from '../../../constants/posMenu';
import { selectPrinterTagsList } from '../../../redux/selectors';
import { getPrinterTags } from '../../../redux/actions/printers';
import PromotionTab from '../../PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

const CategoryEditor = ({ category, menuMap }) => {
  const dispatch = useDispatch();
  const internalMenuId = useSelector((state) => state.posMenu.internalMenuId);
  const printerTagsList = useSelector(selectPrinterTagsList);
  const [formData, setFormData] = useState(DEFAULT_CATEGORY);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldSavingStatus, setFieldSavingStatus] = useState({});
  const saveTimeoutsRef = useRef({}); // Stores { timeoutId, saveCallback }
  const scheduleTimeout = useScheduledTimeouts();
  const [sortModalOpen, setSortModalOpen] = useState(false);

  const isEditing = category && category._id;
  const promotionsEnabled = usePromotionsEnabled();

  // Get the fresh category from menuMap to have the latest itemIds
  const currentCategory =
    isEditing && menuMap?.categories?.[category._id]
      ? menuMap.categories[category._id]
      : category;

  // Count only items that actually exist in menuMap (matching MenuTree behavior)
  const existingItemsCount = (currentCategory?.itemIds || []).filter(
    (itemId) => menuMap?.items?.[itemId]
  ).length;

  // Track the last initialized category ID to prevent re-initialization on menuMap changes
  const lastInitializedCategoryIdRef = useRef(null);

  useEffect(() => {
    // Only initialize form when category ID changes, not when menuMap updates
    const currentCategoryId = category?._id || null;
    if (
      currentCategoryId &&
      currentCategoryId === lastInitializedCategoryIdRef.current
    ) {
      return; // Skip re-initialization if same existing category
    }
    lastInitializedCategoryIdRef.current = currentCategoryId;

    if (category && category._id) {
      // Get the fresh category data from menuMap if available
      const freshCategory = menuMap?.categories?.[category._id] || category;
      setFormData(freshCategory);
    } else {
      setFormData(DEFAULT_CATEGORY);
    }
  }, [category, menuMap]);

  useEffect(() => {
    return () => {
      Object.values(saveTimeoutsRef.current).forEach((pending) => {
        if (pending?.timeoutId) clearTimeout(pending.timeoutId);
        if (pending?.saveCallback) pending.saveCallback();
      });
      saveTimeoutsRef.current = {};
    };
  }, [category?._id]);

  // Fetch printer tags
  useEffect(() => {
    dispatch(getPrinterTags());
  }, [dispatch]);

  const handleSave = async () => {
    try {
      if (!formData.name?.en?.trim()) {
        toast.error('Name is Required');
        return;
      }

      setIsSaving(true);
      if (category && category._id) {
        await dispatch(updateCategory(category._id, formData));
        toast.success('Category updated successfully');
      } else {
        // Validate that internalMenuId is available for new categories
        if (!internalMenuId) {
          toast.error(
            'Menu information is not loaded. Please refresh the page.'
          );
          return;
        }
        // Add internal menu ID to formData for new categories
        const createdCategory = await dispatch(
          createCategory({ ...formData, menu: internalMenuId })
        );
        toast.success('Category created successfully');

        // After creating, select the newly created category to show it in editor
        dispatch(setSelectedEntity(createdCategory, 'category', true));
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!category || !category._id) return;

    const confirmed = await confirm({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete "${
        formData.name?.en || 'this category'
      }"? All items in this category will be unlinked. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColor: 'error',
    });

    if (!confirmed) return;

    try {
      await dispatch(deleteCategory(category._id));
      dispatch(clearSelected());
      toast.success('Category deleted successfully');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  const isFieldValueEqual = (newValue, storedValue) => {
    if (newValue === storedValue) return true;
    if (newValue == null && storedValue == null) return true;
    if (newValue == null || storedValue == null) return false;
    if (
      typeof newValue === 'object' &&
      'en' in newValue &&
      typeof storedValue === 'object'
    ) {
      return (newValue.en || '') === (storedValue.en || '');
    }
    return JSON.stringify(newValue) === JSON.stringify(storedValue);
  };

  // Auto-save handler for existing categories
  const handleFieldAutoSave = async (fieldName, value) => {
    if (!isEditing) return;

    // Cancel the pending save before the unchanged check, otherwise toggling a
    // value back within the debounce window would still send the stale value
    const pendingSave = saveTimeoutsRef.current[fieldName];
    if (pendingSave?.timeoutId) {
      clearTimeout(pendingSave.timeoutId);
      saveTimeoutsRef.current[fieldName] = null;
    }

    // Skip save if value hasn't changed from the stored value
    const storedCategory = menuMap?.categories?.[category._id];
    if (storedCategory && isFieldValueEqual(value, storedCategory[fieldName])) {
      if (pendingSave) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
      }
      return;
    }

    // Set saving status
    setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saving' }));

    // Create the save callback that can be called immediately on unmount or after debounce
    const saveCallback = async () => {
      try {
        // Validation
        if (fieldName === 'name' && !value?.en?.trim()) {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
          toast.error('Name is Required');
          return;
        }

        // Prepare update data
        const updateData = { [fieldName]: value };

        // Send update
        await dispatch(updateCategory(category._id, updateData));

        setFormData((prev) => {
          if (prev._id !== category._id) return prev;
          return { ...prev, [fieldName]: value };
        });

        // Show success status
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saved' }));

        // Clear success indicator after 2 seconds
        scheduleTimeout(() => {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
        }, 2000);
      } catch (error) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
        toast.error(`Failed to save ${fieldName}: ${error.message}`);
      }
    };

    // Debounce the save by 300ms. Once it fires it is no longer pending, so an
    // unmount during the request must not send it again.
    const timeoutId = setTimeout(() => {
      saveTimeoutsRef.current[fieldName] = null;
      saveCallback();
    }, 300);

    // Store both timeout ID and save callback in ref
    saveTimeoutsRef.current[fieldName] = { timeoutId, saveCallback };
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#141414',
              mb: 0.5,
            }}
          >
            {isEditing ? 'Edit Category' : 'New Category'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: '#9E9EAD',
            }}
          >
            {isEditing
              ? 'Manage category details and settings'
              : 'Create a new category for your menu'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isEditing && (
            <>
              <Button
                variant="outlined"
                startIcon={<SwapVertIcon />}
                onClick={() => setSortModalOpen(true)}
                disabled={!existingItemsCount}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  px: 3,
                }}
              >
                Sort Items
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  px: 3,
                }}
              >
                Delete
              </Button>
            </>
          )}
          {!isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                px: 4,
              }}
            >
              {isSaving ? 'Saving...' : 'Save Category'}
            </Button>
          )}
        </Box>
      </Box>

      <Card sx={{ boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ px: 4, py: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#141414',
              mb: 1,
            }}
          >
            Basic Information
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#9E9EAD',
              mb: 3,
            }}
          >
            Configure the basic details for this category
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}
          >
            <TranslationInput
              label="Category Name"
              value={formData.name || { en: '', es: '' }}
              onChange={(value) => setFormData({ ...formData, name: value })}
              onBlur={(value) => handleFieldAutoSave('name', value)}
              required
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.name} />
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}
          >
            <TranslationInput
              label="Description"
              value={formData.description || { en: '', es: '' }}
              onChange={(value) =>
                setFormData({ ...formData, description: value })
              }
              onBlur={(value) => handleFieldAutoSave('description', value)}
              multiline
              rows={4}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.description} />
            </Box>
          </Box>

          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive !== false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({ ...formData, isActive: newValue });
                    if (isEditing) {
                      handleFieldAutoSave('isActive', newValue);
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Active
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Show this category in the menu
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ minWidth: 24 }}>
              <FieldSavingIndicator status={fieldSavingStatus.isActive} />
            </Box>
          </Box>

          {/* Printer Tags */}
          <Box
            sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="printer-tags-label" sx={{ background: 'white' }}>
                Printer Tags
              </InputLabel>
              <SearchableSelect
                labelId="printer-tags-label"
                multiple
                value={formData.printerTags || []}
                onChange={(e) => {
                  let value = e.target.value;
                  // Handle "No Tag" selection - clear all tags
                  if (value.includes('__no_tag__')) {
                    value = [];
                  }
                  setFormData({ ...formData, printerTags: value });
                  if (isEditing) {
                    handleFieldAutoSave('printerTags', value);
                  }
                }}
                input={<OutlinedInput label="Printer Tags" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography
                        sx={{ color: 'text.secondary', fontSize: '14px' }}
                      >
                        No Tag
                      </Typography>
                    ) : (
                      <>
                        {selected.slice(0, 5).map((val) => {
                          const found = printerTagsList?.find(
                            (item) => item?.tagName === val
                          );
                          return (
                            <Chip
                              key={val}
                              sx={{
                                height: '26px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth:
                                  found?.tagName?.length > 10
                                    ? '110px'
                                    : 'inherit',
                              }}
                              label={found?.tagName || val}
                              onDelete={(event) => {
                                event.stopPropagation();
                                const updatedValue = (
                                  formData.printerTags || []
                                ).filter((item) => item !== val);
                                setFormData({
                                  ...formData,
                                  printerTags: updatedValue,
                                });
                                if (isEditing) {
                                  handleFieldAutoSave(
                                    'printerTags',
                                    updatedValue
                                  );
                                }
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              size="small"
                            />
                          );
                        })}
                        {selected.length > 5 && (
                          <Chip
                            sx={{ height: '26px', fontWeight: 500 }}
                            label={`+${selected.length - 5} more`}
                            size="small"
                          />
                        )}
                      </>
                    )}
                  </Box>
                )}
                sx={{
                  '& .MuiSelect-select': {
                    minHeight: '28px',
                  },
                }}
              >
                <MenuItem value="__no_tag__">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                        >
                          No Tag
                        </Typography>
                      }
                    />
                  </Box>
                </MenuItem>
                {printerTagsList?.map((tag) => (
                  <MenuItem key={tag._id || tag.tagName} value={tag.tagName}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <ListItemText
                        disableTypography
                        primary={<Typography>{tag.tagName}</Typography>}
                      />
                      {(formData.printerTags || []).includes(tag.tagName) && (
                        <CheckIcon
                          fontSize="small"
                          sx={{ color: 'rgba(29, 25, 43, 1)' }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.printerTags} />
            </Box>
          </Box>

          {isEditing && existingItemsCount > 0 && (
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '13px',
                  color: '#626A7A',
                  fontWeight: 500,
                }}
              >
                This category contains {existingItemsCount} item
                {existingItemsCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Promotions Section */}
      {isEditing && promotionsEnabled && (
        <Card sx={{ mt: 3, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: '400', fontSize: '18px', mb: 2 }}>
              Promotions
            </Typography>
            <PromotionTab
              lockedCategoryIds={[category.uuid]}
              lockedCategoryName={getTranslationValue(category?.name)}
            />
          </CardContent>
        </Card>
      )}

      {/* Item Sort Modal */}
      <ItemSortModal
        open={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
        category={currentCategory}
        menuMap={menuMap}
      />
    </Box>
  );
};

export default CategoryEditor;
